/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/* eslint-disable no-param-reassign */
import Framework from '@greenville/framework';
import { applySnapshot, getSnapshot, types } from 'mobx-state-tree';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';

const DataItem = types.model('TelemetryDataItem', {
  traceId: types.maybeNull(types.string),
  tenantId: types.maybeNull(types.string),
  serviceName: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.number),
  userInput: types.maybeNull(types.string),
  primaryId: types.maybeNull(types.string)
});

const TelemetrySearchModel = types
  .model('TelemetrySearchModel', {
    data: types.optional(types.array(DataItem), []),
    totalCount: types.optional(types.union(types.number, types.null), 0),
    totalPages: types.optional(types.union(types.number, types.null), 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    isLoading: types.optional(types.boolean, false),
    hasMorePages: types.optional(types.boolean, false),
    lastPayload: types.optional(types.frozen(), {}),
    paginationUsesDateRange: types.optional(types.boolean, false),
    lastSearchedParams: types.optional(types.maybeNull(types.frozen()), null),
    initialSnapshot: types.optional(types.frozen(), {})
  })
  .views((self) => ({
    get telemetryData() {
      return self.data;
    },
    get hasData() {
      return self.data.length > 0;
    },
    get totalItems() {
      return self.totalCount;
    },
    get currentPageNumber() {
      return self.currentPage;
    },
    get totalPagesCount() {
      return self.totalPages;
    },
    get canLoadNextPage() {
      return self.hasMorePages && !self.isLoading;
    },
    get itemsPerPage() {
      return self.pageSize;
    },
    get isFirstPage() {
      return self.currentPage === 0;
    }
  }))
  .actions((self) => {
    const publishResponse = () => {
      Framework.getEventManager().publish(constants.GET_TELEMETRY_SEARCH_RESPONSE);
    };

    return {
      setLoading(loading) {
        self.isLoading = loading;
      },
      setPageSize(size) {
        self.pageSize = size;
      },
      setLastPayload(payload) {
        self.lastPayload = payload;
      },
      setTotalCount(count) {
        self.totalCount = count;
      },
      setTotalPages(pages) {
        self.totalPages = pages;
      },
      setCurrentPage(page) {
        self.currentPage = page;
      },
      setHasMorePages(value) {
        self.hasMorePages = value;
      },

      fetchInitial(payload = {}) {
        self.lastSearchedParams = null;
        self.resetData();
        self.paginationUsesDateRange = Boolean(payload.startDate && payload.endDate);
        self.setLastPayload(payload);
        self.setLoading(true);

        const requestPayload = {
          ...payload,
          offset: 0,
          limit: self.pageSize
        };
        if (payload.startDate != null && payload.startDate !== '') {
          requestPayload.startDate = payload.startDate;
        }
        if (payload.endDate != null && payload.endDate !== '') {
          requestPayload.endDate = payload.endDate;
        }

        Framework.getEventManager().publish(constants.GET_TELEMETRY_SEARCH_DATA, {
          payload: requestPayload
        });
      },

      fetchPage(page, limit = null) {
        const pageSize = limit || self.pageSize;
        const offset = page * pageSize;

        let requestPayload = {
          ...self.lastPayload,
          offset,
          limit: pageSize
        };

        const serverEndDate = self.lastSearchedParams?.endDate;
        if (self.paginationUsesDateRange && serverEndDate != null && serverEndDate !== '') {
          requestPayload = {
            ...requestPayload,
            endDate: serverEndDate
          };
        }

        self.setPageSize(pageSize);
        self.setLoading(true);

        Framework.getEventManager().publish(constants.GET_TELEMETRY_SEARCH_DATA, {
          payload: requestPayload
        });
      },

      setResponse(response) {
        if (!response) {
          publishResponse();
          return;
        }

        let responseData = [];
        let paginationData = null;
        let searchedParams = null;

        if (response.success !== undefined) {
          responseData = response.data || [];
          paginationData = response.pagination || null;
          searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
        } else if (Array.isArray(response)) {
          responseData = response;
        } else if (response.data) {
          responseData = response.data;
          paginationData = response.pagination || null;
          searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
        }

        const enrichedData = responseData.map((item) => ({
          ...item,
          createdDate: utils.toEpochUTC(item.serverDateTime)
        }));

        const hadExistingRows = self.data.length > 0;
        if (enrichedData.length === 0 && hadExistingRows) {
          self.setHasMorePages(false);
          self.setLoading(false);
          publishResponse();
          return;
        }

        applySnapshot(self.data, enrichedData);

        if (searchedParams && typeof searchedParams === 'object' && !Array.isArray(searchedParams)) {
          self.lastSearchedParams = { ...searchedParams };
        }

        if (paginationData) {
          if (paginationData.totalCount === null) {
            self.setTotalCount(null);
          } else {
            self.setTotalCount(paginationData.totalCount ?? 0);
          }
          if (paginationData.totalPages === null) {
            self.setTotalPages(null);
          } else {
            self.setTotalPages(paginationData.totalPages ?? 0);
          }
          if (paginationData.hasMorePages !== undefined) {
            self.setHasMorePages(Boolean(paginationData.hasMorePages));
          } else if (paginationData.totalCount === null) {
            self.setHasMorePages(true);
          } else {
            self.setHasMorePages(false);
          }

          const currentPageIndex =
            paginationData.offset !== undefined && paginationData.limit
              ? Math.floor(paginationData.offset / paginationData.limit)
              : 0;
          self.setCurrentPage(currentPageIndex);
        } else {
          self.setTotalCount(responseData.length);
          self.setTotalPages(1);
          self.setHasMorePages(false);
          self.setCurrentPage(0);
        }

        self.setLoading(false);
        publishResponse();
      },

      resetData() {
        applySnapshot(self.data, []);
        self.setTotalCount(0);
        self.setTotalPages(0);
        self.setCurrentPage(0);
        self.setHasMorePages(false);
        self.setLastPayload({});
        self.paginationUsesDateRange = false;
        self.lastSearchedParams = null;
      },

      resetStoreValues() {
        self.setLoading(false);
        self.resetData();
        publishResponse();
      },

      afterCreate() {
        self.initialSnapshot = getSnapshot(self);
      }
    };
  });

export default TelemetrySearchModel;
